/**
 * Theme type surface.
 *
 * Axes are orthogonal:
 *   - mode     (light ↔ dark)        — the base visual theme
 *   - contrast (normal ↔ high)       — stackable modifier on either mode
 *   - accent   (brand colour)        — drives surfaces, borders, and shadows
 *   - autoMode (manual ↔ system)     — how mode is resolved at runtime
 *   - motion   (full ↔ reduced)      — optional override of OS motion pref
 */

export type ThemeMode = 'light' | 'dark';
export type ContrastLevel = 'normal' | 'high';
export type AccentKey = 'slate' | 'sage' | 'plum' | 'rust' | 'ochre' | 'custom';
export type AutoMode = 'manual' | 'system';
export type MotionPref = 'system' | 'full' | 'reduced';

export interface ThemePrefs {
  /** User-selected base mode; applied as-is when autoMode === 'manual'. */
  mode: ThemeMode;
  /** High-contrast is a modifier on top of the chosen mode. */
  contrast: ContrastLevel;
  accent: AccentKey;
  autoMode: AutoMode;
  motion: MotionPref;
  /**
   * Height of the accent-tinted bottom gradient, in percent of viewport.
   * 0 = flat background (gradient off). 50 = gradient fills the lower half.
   * Default 18 matches the historical "mid at 82%" stop position.
   */
  gradientHeight: number;
  /**
   * Library of background images. Client-only for now; persisted as data
   * URLs inside localStorage. The user picks which one is active via
   * `activeBgImageId`.
   */
  bgImages: BgImageEntry[];
  /** Id of the currently-applied background image, or `null` for none. */
  activeBgImageId: string | null;
  /** How the active background image is laid out inside the viewport. */
  bgImageFit: BgImageFit;
  /**
   * Tile size (vw %) used when `bgImageFit === 'tile'`. 5 = tiny tiles,
   * 60 = ~half-viewport tiles. Value is applied as `XXvw` on
   * `background-size`.
   */
  bgTileSize: number;
  /** Hex for the custom accent slot. Applied only when accent === 'custom'. */
  customAccentHex: string;
}

export interface BgImageEntry {
  id: string;
  dataUrl: string;
  /** Original filename (display-only). */
  name: string;
  /** Unix ms; sort newest first in the thumbnail strip. */
  createdAt: number;
}

export type BgImageFit = 'cover' | 'contain' | 'fill' | 'tile' | 'center';

export const TILE_SIZE_MIN = 5;
export const TILE_SIZE_MAX = 60;
export const TILE_SIZE_DEFAULT = 15;

export const BG_IMAGE_FITS: readonly {
  key: BgImageFit;
  label: string;
  hint: string;
}[] = [
  { key: 'cover', label: 'Fill', hint: 'Cover the viewport, crop edges' },
  { key: 'contain', label: 'Fit', hint: 'Fit entirely, may letterbox' },
  { key: 'fill', label: 'Stretch', hint: 'Stretch to exact viewport' },
  { key: 'tile', label: 'Tile', hint: 'Repeat at natural size' },
  { key: 'center', label: 'Center', hint: 'Centered, no scaling' },
] as const;

export const GRADIENT_HEIGHT_MIN = 0;
export const GRADIENT_HEIGHT_MAX = 50;
export const GRADIENT_HEIGHT_DEFAULT = 0;

export const DEFAULT_CUSTOM_ACCENT_HEX = '#093786';

export const DEFAULT_PREFS: ThemePrefs = {
  mode: 'light',
  contrast: 'normal',
  accent: 'custom',
  autoMode: 'manual',
  motion: 'system',
  gradientHeight: GRADIENT_HEIGHT_DEFAULT,
  bgImages: [],
  activeBgImageId: null,
  bgImageFit: 'cover',
  bgTileSize: TILE_SIZE_DEFAULT,
  customAccentHex: DEFAULT_CUSTOM_ACCENT_HEX,
};

/**
 * Grounded, neutral palette — muted tones that sit well alongside content
 * rather than competing with it. The sixth slot (`custom`) is user-picked
 * and resolves via `prefs.customAccentHex`.
 */
export const ACCENTS: readonly {
  key: AccentKey;
  label: string;
  chip: string;
  rgb: string;
}[] = [
  { key: 'slate', label: 'Slate', chip: '#7c869a', rgb: '124 134 154' },
  { key: 'sage', label: 'Sage', chip: '#96aa91', rgb: '150 170 145' },
  { key: 'plum', label: 'Plum', chip: '#a28a9e', rgb: '162 138 158' },
  { key: 'rust', label: 'Rust', chip: '#c28c76', rgb: '194 140 118' },
  { key: 'ochre', label: 'Ochre', chip: '#ceb282', rgb: '206 178 130' },
] as const;

export const MODES: readonly ThemeMode[] = ['light', 'dark'] as const;

export const STORAGE_KEY = 'ct-agent:theme:v2';
